import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  FaArrowRight,
  FaCheck,
  FaChartLine,
  FaClock,
  FaCode,
  FaCrown,
  FaLightbulb,
  FaMobileAlt,
  FaRegStar,
  FaRocket,
  FaSearch,
  FaShieldAlt,
  FaUsers
} from "react-icons/fa";
import PageAnimWrapper from "../../components/pagetransition";
import Head from "../Head";
import { apiUrl, getServiceData, getServiceDetailData } from "../../api/api";
import { imagePath } from "../../utils/assetUtils";
import {
  fallbackServices,
  findServiceBySlug,
  serviceDescription,
  serviceDetailDescription,
  serviceImageUrl,
  serviceLink
} from "./serviceData";
import "./services.scss";

const hasValue = value => value !== undefined && value !== null && value !== "";

const asArray = value => (Array.isArray(value) ? value.filter(Boolean) : []);

const pickText = (item, keys, fallback = "") => {
  if (!item) {
    return fallback;
  }

  const key = keys.find(field => hasValue(item[field]));
  return key ? item[key] : fallback;
};

const pickMedia = (item, keys) => {
  if (!item) {
    return "";
  }

  const key = keys.find(field => hasValue(item[field]));

  if (!key) {
    return "";
  }

  const value = item[key];

  if (typeof value === "object") {
    return pickText(value, ["url", "src", "path", "file", "filename"]);
  }

  return value;
};

const optionalText = (value, fallback, useFallback) => {
  if (useFallback) {
    return hasValue(value) ? value : fallback;
  }

  if (!hasValue(value) || value === fallback) {
    return "";
  }

  return value;
};

const mediaUrl = value => {
  if (!hasValue(value)) {
    return "";
  }

  const url = String(value).trim();

  if (!url) {
    return "";
  }

  if (/^https?:\/\//i.test(url)) {
    return url;
  }

  if (url.charAt(0) === "/") {
    return `${apiUrl}${url}`;
  }

  if (url.indexOf("/") !== -1) {
    return `${apiUrl}/${url.replace(/^\/+/, "")}`;
  }

  return `${apiUrl}/images/services/${url}`;
};

const iconSet = [
  <FaRegStar />,
  <FaUsers />,
  <FaCrown />,
  <FaChartLine />,
  <FaShieldAlt />,
  <FaCode />,
  <FaMobileAlt />,
  <FaClock />
];

const normalizeCards = (items, fallbackItems = []) => {
  const source = asArray(items);

  if (!source.length) {
    return fallbackItems;
  }

  return source.map((item, index) => ({
    icon: iconSet[index % iconSet.length],
    iconImage: mediaUrl(
      pickMedia(item, [
        "icon",
        "icon_file",
        "icon_image",
        "icon_img",
        "icon_path",
        "icon_name",
        "image",
        "image_icon",
        "upload_icon",
        "uploaded_icon",
        "benefit_icon",
        "trust_icon",
        "service_icon",
        "detail_icon",
        "icon_url",
        "icon_src"
      ])
    ),
    title: pickText(item, ["title", "heading", "name", "label"]),
    text: pickText(item, ["text", "description", "content", "answer"])
  }));
};

const normalizeMetrics = (items, fallbackItems = []) => {
  const source = asArray(items);

  if (!source.length) {
    return fallbackItems;
  }

  return source.map(item => ({
    value: pickText(item, ["value", "number", "count", "title"]),
    label: pickText(item, ["label", "text", "description"])
  }));
};

const normalizeProcess = (items, fallbackItems = []) => {
  const source = asArray(items);

  if (!source.length) {
    return fallbackItems;
  }

  return source.map((item, index) => ({
    step: pickText(
      item,
      ["step", "number"],
      String(index + 1).padStart(2, "0")
    ),
    title: pickText(item, ["title", "heading", "name"]),
    text: pickText(item, ["text", "description", "content"])
  }));
};

const normalizeCapabilities = (items, fallbackItems = []) => {
  const source = asArray(items);

  if (!source.length) {
    return fallbackItems;
  }

  return source
    .map(item =>
      typeof item === "string"
        ? item
        : pickText(item, ["title", "label", "name", "text"])
    )
    .filter(Boolean);
};

const normalizeFaqs = (items, fallbackItems = []) => {
  const source = asArray(items);

  if (!source.length) {
    return fallbackItems;
  }

  return source.map(item => ({
    question: pickText(item, ["question", "title", "heading"]),
    answer: pickText(item, ["answer", "text", "description", "content"])
  }));
};

export default function ServiceDetail() {
  const { slug } = useParams();
  const [services, setServices] = useState(fallbackServices);
  const [serviceDetail, setServiceDetail] = useState(null);
  const [isLoading, setLoading] = useState(true);
  const [openFaqIndex, setOpenFaqIndex] = useState(0);

  useEffect(() => {
    let isMounted = true;

    getServiceData()
      .then(servicecol => {
        let nextServices = fallbackServices;

        if (Array.isArray(servicecol) && servicecol.length) {
          nextServices = servicecol;
        }

        const matchedService = findServiceBySlug(nextServices, slug);

        if (isMounted) {
          setServices(nextServices);
          setServiceDetail(matchedService || null);
        }

        if (!matchedService || !matchedService.id) {
          return null;
        }

        return getServiceDetailData(matchedService.id)
          .then(detailResponse => {
            const detailData =
              detailResponse && detailResponse.status === 1
                ? detailResponse.data
                : detailResponse;

            if (isMounted && detailData) {
              setServiceDetail({
                ...matchedService,
                ...detailData,
                __hasDetailApi: true
              });
            }
          })
          .catch(() => {
            if (isMounted) {
              setServiceDetail(matchedService);
            }
          });
      })
      .catch(() => {
        const matchedService = findServiceBySlug(fallbackServices, slug);

        if (isMounted) {
          setServices(fallbackServices);
          setServiceDetail(matchedService || null);
        }
      })
      .finally(() => {
        if (isMounted) {
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [slug]);

  const service = serviceDetail || findServiceBySlug(services, slug);

  if (!service && isLoading) {
    return (
      <PageAnimWrapper>
        <div className="service-detail-page service-detail-page--missing">
          <section className="service-detail-missing">
            <p className="eyebrow">Loading</p>
            <h1>Loading service details.</h1>
          </section>
        </div>
      </PageAnimWrapper>
    );
  }

  if (!service) {
    return (
      <PageAnimWrapper>
        <Head
          title="Service Not Found | Makemelive Technologies"
          description="Makemelive Technologies services"
          url={`https://makemelive.in/services/${slug || ""}`}
          type="website"
          site_name="Makemelive Technologies"
        />
        <div className="service-detail-page service-detail-page--missing">
          <section className="service-detail-missing">
            <p className="eyebrow">Service</p>
            <h1>We could not find this service.</h1>
            <p>
              Explore our services and choose the solution that fits your next
              project.
            </p>
            <Link to="/services" className="cybr-btn cybr-btn--sm">
              View Services
              <span aria-hidden className="cybr-btn__glitch">
                View Services
              </span>
            </Link>
          </section>
        </div>
      </PageAnimWrapper>
    );
  }

  const title = (
    service.service_title ||
    service.title ||
    service.name ||
    ""
  ).trim();
  const description = serviceDescription(service);
  const detailDescription = serviceDetailDescription(service);
  const imageUrl = serviceImageUrl(service);
  const rawVideoUrl =
    service.service_video || service.video_url || service.video || "";
  const videoUrl =
    rawVideoUrl && /^https?:\/\//i.test(rawVideoUrl)
      ? rawVideoUrl
      : rawVideoUrl
      ? `${apiUrl}/images/services/${rawVideoUrl}`
      : "";
  const otherServices = services.filter(
    item => String(item.id) !== String(service.id)
  );
  const fallbackMetrics = [
    { value: "15+", label: "years of delivery thinking" },
    { value: "500+", label: "digital builds shipped" },
    { value: "4x", label: "focus on speed, UX, SEO, and scale" }
  ];
  const fallbackBenefitItems = [
    {
      icon: <FaRegStar />,
      title: "First Impressions Matter",
      text:
        "A refined digital experience builds trust quickly and helps your audience understand why your brand is the right choice."
    },
    {
      icon: <FaUsers />,
      title: "User Experience",
      text:
        "Clear navigation, responsive layouts, and purposeful content make every interaction easier for your customers."
    },
    {
      icon: <FaCrown />,
      title: "Brand Identity",
      text:
        "The service experience is shaped around your visual language, tone, market position, and long-term business goals."
    },
    {
      icon: <FaChartLine />,
      title: "Performance Growth",
      text:
        "Strong structure, speed, and discoverability help your digital presence support enquiries, sales, and repeat visits."
    }
  ];

  const fallbackTrustItems = [
    {
      icon: <FaShieldAlt />,
      title: "Industry Knowledge",
      text:
        "Strategy shaped by real project delivery across brands, startups, and growth teams."
    },
    {
      icon: <FaCode />,
      title: "Clean Execution",
      text:
        "Modern frontend, reliable integrations, and practical systems your team can maintain."
    },
    {
      icon: <FaMobileAlt />,
      title: "Responsive Design",
      text:
        "Every layout is planned for mobile, tablet, desktop, and real customer behaviour."
    },
    {
      icon: <FaClock />,
      title: "Launch Support",
      text:
        "From QA to go-live, the handover stays structured, transparent, and accountable."
    }
  ];

  const fallbackProcessItems = [
    {
      step: "01",
      title: "Discovery Sprint",
      text:
        "We map goals, audience intent, competitors, content needs, and technical realities before deciding the direction."
    },
    {
      step: "02",
      title: "Experience Architecture",
      text:
        "The flow is planned around clarity, conversion, and the fastest path from first impression to action."
    },
    {
      step: "03",
      title: "Design And Build",
      text:
        "Visual design, responsive UI, frontend implementation, and integrations move together so the output stays sharp."
    },
    {
      step: "04",
      title: "QA And Launch",
      text:
        "Before go-live, we check performance, responsive behaviour, content polish, analytics readiness, and handover details."
    }
  ];

  const fallbackCapabilityItems = [
    "Strategy",
    "UX Direction",
    "Responsive UI",
    "Performance",
    "SEO Structure",
    "Conversion Flow",
    "API Integration",
    "Launch Support"
  ];

  const fallbackFaqItems = [
    {
      question: `What does your ${title} service include?`,
      answer:
        "It includes planning, experience direction, implementation support, quality checks, and launch guidance based on your project scope."
    },
    {
      question: "How do you understand the business requirement?",
      answer:
        "We begin with your audience, current digital presence, competition, goals, and the actions you want users to take."
    },
    {
      question: "Can this service be customized for my brand?",
      answer:
        "Yes. The structure, visuals, content priority, and technical approach are adapted to your brand and business model."
    },
    {
      question: "How do I start the project?",
      answer:
        "Share your requirement through the contact page or call us. We will review the scope and suggest the next practical step."
    }
  ];
  const isFallbackService = fallbackServices.some(item => item === service);
  const sectionFallback = isFallbackService ? undefined : [];
  const defaultHeroEyebrow = "Makemelive Service";
  const defaultProofLabel = "Premium Delivery";
  const defaultProofTitle =
    "Strategy, design, technology, and growth in one flow";
  const defaultBenefitsEyebrow = "Why It Matters";
  const defaultBenefitsHeading =
    "Digital experiences that make your brand easier to trust";
  const defaultVideoEyebrow = "Project Preview";
  const defaultVideoHeading =
    "See the experience before it becomes a full build";
  const defaultVideoDescription =
    "Every strong digital product begins with a clear story. This section is designed as a premium video area for your service overview, brand film, walkthrough, or campaign introduction.";
  const defaultSplitEyebrow = "Built By Specialists";
  const defaultSplitHeading = `We shape ${title.toLowerCase()} with clarity and commercial focus`;
  const defaultSplitDescription =
    "Our team combines design thinking, development discipline, and marketing awareness so the final experience does more than look polished. It communicates clearly, performs reliably, and supports measurable action.";
  const defaultProcessEyebrow = "How We Build";
  const defaultProcessHeading =
    "A calm, structured process from first call to launch";
  const defaultTrustEyebrow = "Why Choose Us";
  const defaultTrustHeading =
    "A practical, premium delivery system for ambitious brands";
  const defaultFaqEyebrow = "FAQ";
  const defaultFaqHeading = "Questions clients usually ask before starting";
  const heroEyebrow = optionalText(
    service.detail_hero_eyebrow,
    defaultHeroEyebrow,
    isFallbackService
  );
  const proofLabel = optionalText(
    service.detail_proof_label,
    defaultProofLabel,
    isFallbackService
  );
  const proofTitle = optionalText(
    service.detail_proof_title,
    defaultProofTitle,
    isFallbackService
  );
  const benefitsEyebrow = optionalText(
    service.detail_benefits_eyebrow,
    defaultBenefitsEyebrow,
    isFallbackService
  );
  const benefitsHeading = optionalText(
    service.detail_benefits_heading,
    defaultBenefitsHeading,
    isFallbackService
  );
  const videoEyebrow = optionalText(
    service.detail_video_eyebrow,
    defaultVideoEyebrow,
    isFallbackService
  );
  const videoHeading = optionalText(
    service.detail_video_heading,
    defaultVideoHeading,
    isFallbackService
  );
  const videoDescription = optionalText(
    service.detail_video_description,
    defaultVideoDescription,
    isFallbackService
  );
  const splitEyebrow = optionalText(
    service.detail_split_eyebrow,
    defaultSplitEyebrow,
    isFallbackService
  );
  const splitHeading = optionalText(
    service.detail_split_heading,
    defaultSplitHeading,
    isFallbackService
  );
  const splitDescription = optionalText(
    service.detail_split_description,
    defaultSplitDescription,
    isFallbackService
  );
  const processEyebrow = optionalText(
    service.detail_process_eyebrow,
    defaultProcessEyebrow,
    isFallbackService
  );
  const processHeading = optionalText(
    service.detail_process_heading,
    defaultProcessHeading,
    isFallbackService
  );
  const trustEyebrow = optionalText(
    service.detail_trust_eyebrow,
    defaultTrustEyebrow,
    isFallbackService
  );
  const trustHeading = optionalText(
    service.detail_trust_heading,
    defaultTrustHeading,
    isFallbackService
  );
  const faqEyebrow = optionalText(
    service.detail_faq_eyebrow,
    defaultFaqEyebrow,
    isFallbackService
  );
  const faqHeading = optionalText(
    service.detail_faq_heading,
    defaultFaqHeading,
    isFallbackService
  );
  const metrics = normalizeMetrics(
    service.detail_metrics,
    isFallbackService ? fallbackMetrics : sectionFallback
  );
  const benefitItems = normalizeCards(
    service.detail_benefits,
    isFallbackService ? fallbackBenefitItems : sectionFallback
  );
  const trustItems = normalizeCards(
    service.detail_trust,
    isFallbackService ? fallbackTrustItems : sectionFallback
  );
  const processItems = normalizeProcess(
    service.detail_process,
    isFallbackService ? fallbackProcessItems : sectionFallback
  );
  const capabilityItems = normalizeCapabilities(
    service.detail_capabilities,
    isFallbackService ? fallbackCapabilityItems : sectionFallback
  );
  const faqItems = normalizeFaqs(
    service.detail_faqs,
    isFallbackService ? fallbackFaqItems : sectionFallback
  );
  const splitPoints = capabilityItems.slice(0, 3);
  const showBenefits = benefitItems.length > 0;
  const showMetrics = metrics.length > 0;
  const showVideo = Boolean(videoUrl);
  const showSplit =
    hasValue(splitEyebrow) ||
    hasValue(splitHeading) ||
    hasValue(splitDescription) ||
    splitPoints.length > 0;
  const showProcess = processItems.length > 0;
  const showCapabilities = capabilityItems.length > 0;
  const showTrust = trustItems.length > 0;
  const showFaq = faqItems.length > 0;

  return (
    <PageAnimWrapper>
      <Head
        title={`${title} | Makemelive Technologies`}
        ogdescription={description}
        description={description}
        url={`https://makemelive.in/services/${slug}/`}
        type="website"
        site_name="Makemelive Technologies"
      />
      <div className="service-detail-page">
        <section
          className="service-detail-hero"
          style={{ backgroundImage: `url(${imagePath("ut-dot-dark.svg")})` }}
        >
          <div className="service-detail-hero__content">
            <div className="service-detail-hero__copy">
              <Link to="/services" className="service-back-link">
                Services
              </Link>
              {hasValue(heroEyebrow) && (
                <p className="eyebrow">{heroEyebrow}</p>
              )}
              <h1>{title}</h1>
              <p className="lead">{detailDescription}</p>
              <div className="service-detail-actions">
                <Link to="/contact-us" className="cybr-btn cybr-btn--sm">
                  Start a Project
                  <span aria-hidden className="cybr-btn__glitch">
                    Start a Project
                  </span>
                </Link>
                <a href="tel:+919167352347" className="service-call-link">
                  Call +91 91673 52347
                </a>
              </div>
            </div>
            <div
              className="service-detail-hero__visual"
              style={{ backgroundImage: `url(${imageUrl})` }}
            >
              {(hasValue(proofLabel) || hasValue(proofTitle)) && (
                <div className="service-detail-proof">
                  {hasValue(proofLabel) && <span>{proofLabel}</span>}
                  {hasValue(proofTitle) && <strong>{proofTitle}</strong>}
                </div>
              )}
            </div>
          </div>
        </section>

        {showBenefits && (
          <section className="service-detail-section service-detail-benefits">
            <div className="service-section-heading">
              {hasValue(benefitsEyebrow) && (
                <p className="eyebrow">{benefitsEyebrow}</p>
              )}
              {hasValue(benefitsHeading) && <h2>{benefitsHeading}</h2>}
            </div>
            <div className="service-benefit-grid">
              {benefitItems.map(item => (
                <div className="service-benefit-card" key={item.title}>
                  <span>
                    {item.iconImage ? (
                      <img src={item.iconImage} alt="" />
                    ) : (
                      item.icon
                    )}
                  </span>
                  <div>
                    <h3>{item.title}</h3>
                    <p>{item.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {showMetrics && (
          <section className="service-detail-stats">
            {metrics.map(metric => (
              <div className="service-stat-card" key={metric.value}>
                <strong>{metric.value}</strong>
                <span>{metric.label}</span>
              </div>
            ))}
          </section>
        )}

        {showVideo && (
          <section className="service-detail-video">
            <div className="service-detail-video__copy">
              {hasValue(videoEyebrow) && (
                <p className="eyebrow">{videoEyebrow}</p>
              )}
              {hasValue(videoHeading) && <h2>{videoHeading}</h2>}
              {hasValue(videoDescription) && <p>{videoDescription}</p>}
            </div>
            <div className="service-video-frame">
              <video controls poster={imageUrl}>
                <source src={videoUrl} />
              </video>
            </div>
          </section>
        )}

        {showSplit && (
          <section className="service-detail-split">
            <div className="service-detail-split__media">
              <div
                className="service-detail-split__image"
                style={{ backgroundImage: `url(${imageUrl})` }}
              ></div>
            </div>
            <div className="service-detail-split__copy">
              {hasValue(splitEyebrow) && (
                <p className="eyebrow">{splitEyebrow}</p>
              )}
              {hasValue(splitHeading) && <h2>{splitHeading}</h2>}
              {hasValue(splitDescription) && <p>{splitDescription}</p>}
              {splitPoints.length > 0 && (
                <div className="service-split-points">
                  {splitPoints.map((item, index) => (
                    <span key={`${item}-${index}`}>
                      {index === 0 && <FaLightbulb />}
                      {index === 1 && <FaRocket />}
                      {index === 2 && <FaSearch />}
                      {item}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </section>
        )}

        {showProcess && (
          <section className="service-detail-section service-detail-process">
            <div className="service-section-heading">
              {hasValue(processEyebrow) && (
                <p className="eyebrow">{processEyebrow}</p>
              )}
              {hasValue(processHeading) && <h2>{processHeading}</h2>}
            </div>
            <div className="service-process-grid">
              {processItems.map(item => (
                <article className="service-process-card" key={item.step}>
                  <span>{item.step}</span>
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </article>
              ))}
            </div>
          </section>
        )}

        {showCapabilities && (
          <section
            className="service-capability-rail"
            aria-label="Service capabilities"
          >
            <div>
              {capabilityItems.concat(capabilityItems).map((item, index) => (
                <span key={`${item}-${index}`}>{item}</span>
              ))}
            </div>
          </section>
        )}

        {showTrust && (
          <section className="service-detail-section service-detail-trust">
            <div className="service-section-heading">
              {hasValue(trustEyebrow) && (
                <p className="eyebrow">{trustEyebrow}</p>
              )}
              {hasValue(trustHeading) && <h2>{trustHeading}</h2>}
            </div>
            <div className="service-trust-grid">
              {trustItems.map(item => (
                <div className="service-trust-card" key={item.title}>
                  <span>
                    {item.iconImage ? (
                      <img src={item.iconImage} alt="" />
                    ) : (
                      item.icon
                    )}
                  </span>
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {showFaq && (
          <section className="service-detail-section service-detail-faq">
            <div className="service-section-heading">
              {hasValue(faqEyebrow) && <p className="eyebrow">{faqEyebrow}</p>}
              {hasValue(faqHeading) && <h2>{faqHeading}</h2>}
            </div>
            <div className="service-faq-list">
              {faqItems.map((item, index) => {
                const isOpen = openFaqIndex === index;
                const panelId = `service-faq-answer-${index}`;

                return (
                  <div
                    className={`service-faq-item${
                      isOpen ? " service-faq-item--open" : ""
                    }`}
                    key={item.question}
                  >
                    <button
                      type="button"
                      className="service-faq-question"
                      aria-expanded={isOpen}
                      aria-controls={panelId}
                      onClick={() => setOpenFaqIndex(isOpen ? -1 : index)}
                    >
                      <FaCheck />
                      {item.question}
                    </button>
                    <div
                      className="service-faq-answer"
                      id={panelId}
                      role="region"
                      aria-hidden={!isOpen}
                    >
                      <p>{item.answer}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        <section className="service-detail-section service-detail-next">
          <div className="service-section-heading">
            <p className="eyebrow">More Services</p>
            <h2>Explore what else we can build together</h2>
          </div>
          <div className="service-next-grid">
            {otherServices.map(item => (
              <Link
                to={serviceLink(item)}
                className="service-next-card"
                key={item.id || serviceLink(item)}
              >
                <span>
                  {(item.service_title || item.title || item.name || "").trim()}
                </span>
                <FaArrowRight />
              </Link>
            ))}
          </div>
        </section>
      </div>
    </PageAnimWrapper>
  );
}
